import React from 'react';
import { string, func, bool, oneOfType } from 'prop-types';
import classNames from 'classnames';

import { useConfiguration } from '../../context/configurationContext';

import { FormattedMessage, intlShape, injectIntl } from '../../util/reactIntl';
import { displayPrice } from '../../util/configHelpers';
import { lazyLoadWithDimensions } from '../../util/uiHelpers';
import { propTypes } from '../../util/types';
import { formatMoney } from '../../util/currency';
import { ensureListing, ensureUser } from '../../util/data';
import { richText } from '../../util/richText';
import { createSlug } from '../../util/urlHelpers';
import { isBookingProcessAlias } from '../../transactions/transaction';

import { AspectRatioWrapper, NamedLink, ResponsiveImage } from '../../components';

import css from './ListingCard.module.css';
import Icons from '../Icons/Icons';
import { getSubscriptionIcon, getSubscriptionLabel, isHaulStarPlan } from '../../util/genericHelpers';
import { hasDiscounts } from '../../util/listingDiscounts';

const MIN_LENGTH_FOR_LONG_WORDS = 10;

const priceData = (price, currency, intl) => {
  if (price && price.currency === currency) {
    const formattedPrice = formatMoney(intl, price);
    return { formattedPrice, priceTitle: formattedPrice };
  } else if (price) {
    return {
      formattedPrice: intl.formatMessage(
        { id: 'ListingCard.unsupportedPrice' },
        { currency: price.currency }
      ),
      priceTitle: intl.formatMessage(
        { id: 'ListingCard.unsupportedPriceTitle' },
        { currency: price.currency }
      ),
    };
  }
  return {};
};

const LazyImage = lazyLoadWithDimensions(ResponsiveImage, { loadAfterInitialRendering: 3000 });

const PriceMaybe = props => {
  const { price, publicData, config, intl } = props;
  const { listingType } = publicData || {};
  const validListingTypes = config.listing.listingTypes;
  const foundListingTypeConfig = validListingTypes.find(conf => conf.listingType === listingType);
  const showPrice = displayPrice(foundListingTypeConfig);
  if (!showPrice && price) {
    return null;
  }

  const isBookable = isBookingProcessAlias(publicData?.transactionProcessAlias);
  const { formattedPrice, priceTitle } = priceData(price, config.currency, intl);
  return (
    <div className={css.price}>
      <div className={css.priceValue} title={priceTitle}>
        {formattedPrice}
      </div>
      {isBookable ? (
        <div className={css.perUnit}>
          <FormattedMessage id="ListingCard.perUnit" values={{ unitType: publicData?.unitType }} />
        </div>
      ) : null}
    </div>
  );
};

export const ListingCardComponent = props => {
  const config = useConfiguration();
  const {
    className,
    rootClassName,
    intl,
    listing,
    renderSizes,
    setActiveListing,
    showAuthorInfo,
  } = props;
  const classes = classNames(rootClassName || css.root, className);
  const currentListing = ensureListing(listing);
  const id = currentListing.id.uuid;
  const { title = '', price, publicData } = currentListing.attributes;
  const slug = createSlug(title);
  const author = ensureUser(listing.author);
  const authorName = author.attributes.profile.displayName;
  const firstImage =
    currentListing.images && currentListing.images.length > 0 ? currentListing.images[0] : null;

  const {
    aspectWidth = 1,
    aspectHeight = 1,
    variantPrefix = 'listing-card',
  } = config.layout.listingImage;
  const variants = firstImage
    ? Object.keys(firstImage?.attributes?.variants).filter(k => k.startsWith(variantPrefix))
    : [];

  const setActivePropsMaybe = setActiveListing
    ? {
        onMouseEnter: () => setActiveListing(currentListing.id),
        onMouseLeave: () => setActiveListing(null),
      }
    : null;

  const subscriptionName = listing?.attributes?.publicData?.subscriptionProductName;
  const isHaulStarListing = isHaulStarPlan(subscriptionName);

  // Feed/search-card TRUST badges (Listing Richness, 2026-09-27) — Haul Star only.
  // Goal: lift CTR by surfacing trust + discount signals right on the card image;
  // the itemized detail (insurance copy, discount list) lives on the PDP.
  //  • Insured Business + Rental Insurance Available read the OWNER PROFILE
  //    publicData (search now requests profile.publicData; see SearchPage.duck).
  //  • Discounts = a SINGLE generic "Discounts Available" badge (we no longer
  //    stack individual discount pills on the card image).
  // All pure display/CTA ("available" = ask the owner); BigLoads never honors/
  // transacts any of it.
  const ownerProfilePublicData = author?.attributes?.profile?.publicData || {};
  const showInsuredBusiness =
    isHaulStarListing && ownerProfilePublicData.insuredBusiness === true;
  const showRentalInsurance =
    isHaulStarListing && ownerProfilePublicData.rentalInsuranceAvailable === true;
  // Discounts DISPLAY only on Haul Star listings (2026-09-29). ALL owners can
  // SELECT discounts in the create/edit form, but the badge only shows for Haul
  // Star listings (upsell lure); the create form notes this to non-HS owners.
  const showDiscountsBadge =
    isHaulStarListing && hasDiscounts(listing?.attributes?.publicData?.discounts);

  const trustBadges = [];
  if (showInsuredBusiness) {
    // Use the self-colored `insuredBusiness` icon (filled blue shield + white
    // check), NOT `security` — `security` is a transparent black outline that's
    // invisible on the dark badge chip over a variable trailer photo.
    trustBadges.push({ key: 'insured', icon: 'insuredBusiness', label: 'Insured Business' });
  }
  if (showRentalInsurance) {
    trustBadges.push({
      key: 'rental-insurance',
      icon: 'clipboardCheck',
      label: 'Rental Insurance Available',
    });
  }
  if (showDiscountsBadge) {
    trustBadges.push({ key: 'discounts', icon: 'discountTag', label: 'Discounts Available' });
  }

  return (
    <NamedLink
      className={classes}
      /* SEO foundation (2026-07-26): always link the ONE canonical form
         `/l/:slug/:id`. Killed the geo-path branching so feed/search/similar
         cards no longer create duplicate-content URL variants. */
      name="ListingPage"
      params={{ id, slug }}
    >
      <AspectRatioWrapper
        className={css.aspectRatioWrapper}
        width={aspectWidth}
        height={aspectHeight}
        {...setActivePropsMaybe}
      >
        <LazyImage
          rootClassName={css.rootForImage}
          alt={title}
          image={firstImage}
          variants={variants}
          sizes={renderSizes}
        />

        {subscriptionName ? (
          <div className={css.pills}>
            <span>
              <Icons name={getSubscriptionIcon(subscriptionName)} />
              {getSubscriptionLabel(subscriptionName)}
            </span>
          </div>
        ) : null}

        {trustBadges.length > 0 ? (
          <div className={css.trustPills}>
            {trustBadges.map(badge => (
              <span key={badge.key}>
                <Icons name={badge.icon} />
                {badge.label}
              </span>
            ))}
          </div>
        ) : null}
      </AspectRatioWrapper>
      <div className={css.info}>
        <PriceMaybe price={price} publicData={publicData} config={config} intl={intl} />
        <div className={css.mainInfo}>
          <div className={css.title}>
            {richText(title, {
              longWordMinLength: MIN_LENGTH_FOR_LONG_WORDS,
              longWordClass: css.longWord,
            })}
          </div>
          {showAuthorInfo ? (
            <div className={css.authorInfo}>
              <FormattedMessage id="ListingCard.author" values={{ authorName }} />
            </div>
          ) : null}
        </div>
      </div>
    </NamedLink>
  );
};

ListingCardComponent.defaultProps = {
  className: null,
  rootClassName: null,
  renderSizes: null,
  setActiveListing: null,
  showAuthorInfo: true,
};

ListingCardComponent.propTypes = {
  className: string,
  rootClassName: string,
  intl: intlShape.isRequired,
  listing: oneOfType([propTypes.listing, propTypes.ownListing]).isRequired,
  showAuthorInfo: bool,

  // Responsive image sizes hint
  renderSizes: string,

  setActiveListing: func,
};

export default injectIntl(ListingCardComponent);
